<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import Circle1 from "./icons/Circle1.vue";
import Circle2 from "./icons/Circle2.vue";
import Circle3 from "./icons/Circle3.vue";
import Circle4 from "./icons/Circle4.vue";

// The genie stands wherever the hero ends (on phones, however tall that turns out), so
// each ring (with the flowers on it) is moved to circle the present the genie holds
// out. Every ring is drawn centred in its own picture.
const PRESENT = { x: 0.32, y: 0.33 };
const rings = [ref(null), ref(null), ref(null), ref(null)];
const shifts = ref(rings.map(() => ({ x: 0, y: 0 })));

// Each ring's centre comes from where its box is laid out. Each picture is square with
// its ring in the middle, sized so the rings draw at their original sizes.
const box = ref(null);
const align = () => {
  const genie = document.querySelector("#hero img[alt='genie']");
  if (!genie || !box.value) {
    shifts.value = rings.map(() => ({ x: 0, y: 0 }));
    return;
  }
  const g = genie.getBoundingClientRect();
  if (!g.width) return;
  const area = box.value.getBoundingClientRect();
  const target = {
    x: g.left + g.width * PRESENT.x - area.left,
    y: g.top + g.height * PRESENT.y - area.top,
  };
  shifts.value = rings.map((ring) => {
    const el = ring.value;
    if (!el) return { x: 0, y: 0 };
    const style = getComputedStyle(el);
    const width = parseFloat(style.width);
    const height = parseFloat(style.height);
    const left = area.width - parseFloat(style.right) - width;
    const top = parseFloat(style.top);
    return {
      x: Math.round(target.x - (left + width / 2)),
      y: Math.round(target.y - (top + height / 2)),
    };
  });
};
const spin = "h-full w-full will-change-transform motion-reduce:animate-none";
const moved = (index) => {
  const { x, y } = shifts.value[index];
  return x || y ? { transform: `translate(${x}px, ${y}px)` } : undefined;
};

let observer;
let genie;
onMounted(() => {
  // Once the rest of the page has mounted too: the start box's height sets the hero's
  nextTick(align);
  genie = document.querySelector("#hero img[alt='genie']");
  if (window.ResizeObserver) {
    observer = new ResizeObserver(align);
    [document.getElementById("hero"), genie].forEach(
      (el) => el && observer.observe(el)
    );
  }
  genie?.addEventListener("load", align);
  window.addEventListener("resize", align);
  window.addEventListener("hero-resized", align);
});
onBeforeUnmount(() => {
  observer?.disconnect();
  genie?.removeEventListener("load", align);
  window.removeEventListener("resize", align);
  window.removeEventListener("hero-resized", align);
});
</script>

<template>
  <!-- Clipped here: the circles run off the right edge, and a page that can scroll
    sideways gets nudged there when tips or inputs are scrolled into view. On phones
    they're also kept inside the hero, and on computers inside the first screen (the
    white panel covers the rest), where they'd otherwise add empty page below -->
  <div
    ref="box"
    class="absolute inset-0 overflow-hidden lg:bottom-auto lg:h-[100svh]"
  >
    <!-- Each ring sits in a box that's moved to circle the present; the ring turns
      inside it. Turning is left to the graphics chip (a CSS transform), so it stays
      smooth while the page is busy loading -->
    <div
      :ref="rings[0]"
      class="absolute top-[14rem] right-[9rem] h-[180px] w-[180px] lg:right-[8rem] lg:top-[11rem] lg:h-[260px] lg:w-[260px]"
      :style="moved(0)"
    >
      <Circle1 class="h-full w-full" />
    </div>
    <div
      :ref="rings[1]"
      class="absolute right-[3.5rem] top-[7rem] h-[390px] w-[390px] lg:right-[1rem] lg:top-[4rem] lg:h-[556.2px] lg:w-[556.2px]"
      :style="moved(1)"
    >
      <Circle2 :class="spin" class="animate-[spin_25s_linear_infinite]" />
    </div>
    <div
      :ref="rings[2]"
      class="absolute -right-[8.5rem] -top-[9.5rem] h-[557px] w-[557px] lg:-right-[15rem] lg:-top-[13rem] lg:h-[781.4px] lg:w-[781.4px]"
      :style="moved(2)"
    >
      <Circle3
        :class="spin"
        class="animate-[spin_80s_linear_infinite_reverse]"
      />
    </div>
    <div
      :ref="rings[3]"
      class="absolute -right-[9rem] -top-[10rem] h-[763px] w-[763px] lg:-right-[16rem] lg:-top-[13rem] lg:h-[1062px] lg:w-[1062px]"
      :style="moved(3)"
    >
      <Circle4 :class="spin" class="animate-[spin_25s_linear_infinite]" />
    </div>
  </div>
</template>
